import { Controller } from "@hotwired/stimulus"

/**
 * Comparison Controller
 * Manages 3-column product comparison view
 */
export default class extends Controller {
  static targets = ["column", "row", "selectButton", "selectedBadge"]

  static classes = [
    "selected",
    "unselected",
    "buttonSelected",
    "buttonUnselected",
  ]

  static values = {
    selectedProduct: String,
    calculationId: String,
  }

  connect() {
    this.highlightSelectedProduct()
  }

  /**
   * Highlight row on hover
   */
  highlightRow(event) {
    const row = event.currentTarget
    row.classList.add("bg-blue-50")
  }

  /**
   * Remove row highlight
   */
  unhighlightRow(event) {
    const row = event.currentTarget
    row.classList.remove("bg-blue-50")
  }

  /**
   * Select a product from comparison
   */
  selectProduct(event) {
    const button = event.currentTarget
    const productType = button.dataset.productType

    this.selectedProductValue = productType
    this.highlightSelectedProduct()

    // Dispatch custom event for parent controllers
    this.dispatch("selected", {
      detail: {
        productType,
        calculationId: this.calculationIdValue,
      },
    })
  }

  /**
   * Highlight the selected product column
   */
  highlightSelectedProduct() {
    this.columnTargets.forEach((column) => {
      const productType = column.dataset.productType

      if (productType === this.selectedProductValue) {
        this.selectedClasses.forEach((cls) => column.classList.add(cls))
        this.unselectedClasses.forEach((cls) => column.classList.remove(cls))
      } else {
        this.selectedClasses.forEach((cls) => column.classList.remove(cls))
        if (this.selectedProductValue) {
          this.unselectedClasses.forEach((cls) => column.classList.add(cls))
        }
      }
    })

    // Update select buttons
    this.selectButtonTargets.forEach((button) => {
      const productType = button.dataset.productType

      if (productType === this.selectedProductValue) {
        button.textContent = "✓ Selected"
        this.buttonSelectedClasses.forEach((cls) => button.classList.add(cls))
        this.buttonUnselectedClasses.forEach((cls) =>
          button.classList.remove(cls),
        )
      } else {
        button.textContent = "Select"
        this.buttonSelectedClasses.forEach((cls) =>
          button.classList.remove(cls),
        )
        this.buttonUnselectedClasses.forEach((cls) => button.classList.add(cls))
      }
    })

    // Update badges
    this.selectedBadgeTargets.forEach((badge) => {
      const productType = badge.dataset.productType

      if (productType === this.selectedProductValue) {
        badge.classList.remove("hidden")
      } else {
        badge.classList.add("hidden")
      }
    })
  }

  /**
   * Expand details section
   */
  toggleDetails(event) {
    const button = event.currentTarget
    const detailsId = button.dataset.detailsId
    const details = document.getElementById(detailsId)

    if (details) {
      details.classList.toggle("hidden")
      const icon = button.querySelector("svg")
      if (icon) {
        icon.classList.toggle("rotate-180")
      }
    }
  }

  /**
   * Confirm selection and proceed
   */
  confirmSelection() {
    if (!this.selectedProductValue) {
      this.element.dispatchEvent(
        new CustomEvent("toast:show", {
          bubbles: true,
          detail: {
            message: "Please select a product to continue",
            variant: "warning",
          },
        }),
      )
      return
    }

    // Submit form or navigate
    const form = this.element.querySelector("form")
    if (form) {
      const input = form.querySelector("input[name='selected_product']")
      if (input) {
        input.value = this.selectedProductValue
      }
      form.requestSubmit()
    }
  }

  /**
   * Download comparison as PDF
   */
  downloadPdf() {
    const url = new URL(window.location.href)
    url.searchParams.set("format", "pdf")
    window.open(url.toString(), "_blank")
  }
}
